import React from 'react';

const HitRateGauges = ({ hitRateMetrics, handleDrillDown }) => {
  if (!hitRateMetrics) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <div className="text-6xl mb-4 text-gray-300">🎯</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Hit Rate Performance</h3>
        <p className="text-gray-600">No hit rate data available</p>
        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or date range</p>
      </div>
    );
  }

  const gaugeData = [
    { 
      label: 'Perfect Hit Rate', 
      value: hitRateMetrics.perfectHitRate || 0, 
      color: '#7c3aed' 
    },
    { 
      label: 'Tolerance Hit Rate', 
      value: hitRateMetrics.toleranceHitRate || 0, 
      color: '#f59e0b' 
    },
    { 
      label: 'Value Hit Rate', 
      value: hitRateMetrics.valueHitRate || 0, 
      color: '#ef4444' 
    }
  ];

  // Process location hit rates with new structure
  const locationHitRates = hitRateMetrics.locationHitRates ? 
    Object.entries(hitRateMetrics.locationHitRates)
      .map(([location, data]) => ({ 
        location, 
        rate: data.hitRate, 
        locationId: data.locationId 
      }))
      .sort((a, b) => b.rate - a.rate) // Sort by rate descending
    : [];

  // Process product hit rates with new structure  
  const productHitRates = hitRateMetrics.productHitRates ? 
    Object.entries(hitRateMetrics.productHitRates)
      .map(([product, data]) => ({ 
        product, 
        rate: data.hitRate, 
        productId: data.productId 
      }))
      .sort((a, b) => b.rate - a.rate) // Sort by rate descending
    : [];

  const getGaugeColor = (value, target) => {
    if (value >= target) return '#6b7280';
    if (value >= target * 0.8) return '#9ca3af';
    return '#d1d5db';
  };

  const getPerformanceColor = (rate) => {
    if (rate >= 95) return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    if (rate >= 85) return 'bg-blue-50 text-blue-800 border-blue-200';
    if (rate >= 70) return 'bg-amber-50 text-amber-800 border-amber-200';
    if (rate >= 50) return 'bg-orange-50 text-orange-800 border-orange-200';
    return 'bg-red-50 text-red-800 border-red-200';
  };

  return (
    <div className="space-y-6">
      {/* Overall Hit Rate Gauges */}
      <div className="bg-white p-6">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-1">Overall Hit Rate Performance</h3>
          <p className="text-sm text-gray-600">Key performance indicators for inventory accuracy</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {gaugeData.map((gauge, index) => (
              <div key={index} className="text-center bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="relative w-24 h-24 mx-auto mb-4">
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                  {/* Background circle */}
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#f3f4f6"
                    strokeWidth="4"
                  />
                  {/* Progress circle */}
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke={gauge.color}
                    strokeWidth="4"
                    strokeDasharray={`${gauge.value}, 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-bold text-gray-900">
                      {gauge.value.toFixed(1)}%
                    </span>
                </div>
              </div>
                <div className="text-sm font-semibold text-gray-800">{gauge.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Location Hit Rates */}
      {locationHitRates.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Hit Rates by Location</h3>
              <p className="text-sm text-gray-600">Performance breakdown across locations</p>
            </div>
            <span className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-200">
              {locationHitRates.length} Locations
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {locationHitRates.map((item, index) => (
              <div 
                key={index} 
                className="bg-gray-50 border border-gray-200 rounded-lg p-4 transition-all duration-200 hover:shadow-md hover:bg-white cursor-pointer"
                onClick={() => {
                  // Drill down to location hit rate with proper data structure
                  const locationData = {
                    location: item.locationId,
                    locationName: item.location,
                    accuracy: item.rate,
                    isHitRate: true // Flag to indicate this is from hit rate section
                  };
                  handleDrillDown('location', locationData);
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-2xl font-bold text-gray-900">
                    {item.rate.toFixed(1)}%
                  </span>
                  <div className={`w-3 h-3 rounded-full ${
                    item.rate >= 95 ? 'bg-emerald-500' : 
                    item.rate >= 85 ? 'bg-blue-500' : 
                    item.rate >= 70 ? 'bg-amber-500' : 
                    item.rate >= 50 ? 'bg-orange-500' : 'bg-red-500'
                  }`}></div>
                </div>
                <div className="text-sm font-semibold text-gray-800 truncate mb-2" title={item.location}>
                  {item.location}
                </div>
                <div className="text-xs text-gray-600">
                  {item.rate >= 95 ? 'Excellent' : 
                   item.rate >= 85 ? 'Good' : 
                   item.rate >= 70 ? 'Average' : 
                   item.rate >= 50 ? 'Poor' : 'Critical'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product Hit Rates */}
      {productHitRates.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Hit Rates by Product</h3>
              <p className="text-sm text-gray-600">Product performance analysis</p>
            </div>
            <span className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-200">
              {productHitRates.length} Products
            </span>
          </div>
          
          {/* Performance Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Excellent (95%+)', count: productHitRates.filter(p => p.rate >= 95).length, color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
              { label: 'Good (85-94%)', count: productHitRates.filter(p => p.rate >= 85 && p.rate < 95).length, color: 'bg-blue-50 border-blue-200 text-blue-800' },
              { label: 'Average (70-84%)', count: productHitRates.filter(p => p.rate >= 70 && p.rate < 85).length, color: 'bg-amber-50 border-amber-200 text-amber-800' },
              { label: 'Poor (50-69%)', count: productHitRates.filter(p => p.rate >= 50 && p.rate < 70).length, color: 'bg-orange-50 border-orange-200 text-orange-800' },
              { label: 'Critical (<50%)', count: productHitRates.filter(p => p.rate < 50).length, color: 'bg-red-50 border-red-200 text-red-800' }
            ].map((category, index) => (
              <div key={index} className={`border rounded-lg p-4 text-center ${category.color}`}>
                <div className="text-2xl font-bold">{category.count}</div>
                <div className="text-xs font-semibold mt-1">{category.label}</div>
              </div>
            ))}
          </div>

          {/* Product Grid */}
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 divide-y divide-gray-200 md:divide-y-0">
              {productHitRates.map((item, index) => (
                <div 
                  key={index} 
                  className="bg-gray-50 border-r border-gray-200 last:border-r-0 p-4 transition-all duration-200 hover:bg-white hover:shadow-sm cursor-pointer"
                  onClick={() => {
                    // Drill down to product hit rate with proper data structure
                    const productData = {
                      productId: item.productId,
                      product: item.product,
                      accuracy: item.rate,
                      isHitRate: true // Flag to indicate this is from hit rate section
                    };
                    handleDrillDown('product', productData);
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-lg font-bold text-gray-900">
                      {item.rate.toFixed(1)}%
                    </span>
                    <div className={`w-3 h-3 rounded-full ${
                      item.rate >= 95 ? 'bg-emerald-500' : 
                      item.rate >= 85 ? 'bg-blue-500' : 
                      item.rate >= 70 ? 'bg-amber-500' : 
                      item.rate >= 50 ? 'bg-orange-500' : 'bg-red-500'
                    }`}></div>
                  </div>
                  <div className="text-sm font-semibold text-gray-800 line-clamp-2 leading-tight mb-2" title={item.product}>
                    {item.product}
                  </div>
                  <div className="text-xs text-gray-600">
                    {item.rate >= 95 ? 'Excellent' : 
                     item.rate >= 85 ? 'Good' : 
                     item.rate >= 70 ? 'Average' : 
                     item.rate >= 50 ? 'Poor' : 'Critical'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {productHitRates.length > 12 && (
            <div className="text-center mt-4 text-sm text-gray-500">
              <span>Scroll to view all {productHitRates.length} products</span>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {locationHitRates.length === 0 && productHitRates.length === 0 && (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="text-gray-300 text-6xl mb-4">📊</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Detailed Hit Rate Data</h3>
          <p className="text-gray-600">Location and product hit rate breakdown is not available.</p>
          <p className="text-sm text-gray-500 mt-2">Try adjusting your filters or date range to see more data.</p>
        </div>
      )}
    </div>
  );
};

export default HitRateGauges;